/**
 * Turning a table from another system into test cases.
 *
 * Two things vary between exports and are decided here: which column holds
 * which field, and how steps are written down. Both are guessed from the file
 * and both stay editable — a guess that is right most of the time is worth
 * more than a format the user has to produce by hand.
 */
import type { CasePriority } from '@tms/shared';
import type { SheetTable } from './parse-file';

export const IMPORT_FIELDS = [
  'title',
  'description',
  'preconditions',
  'testData',
  'stepAction',
  'stepExpected',
  'priority',
  'labels',
  'environment',
  'layer',
  'module',
  'groupKey',
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number];

/** Column index per field, `null` when the file has nothing for it. */
export type ColumnMapping = Record<ImportField, number | null>;

/**
 * `rows` — a case spans several rows, one per step (BrowserStack, TestRail).
 * `cell` — a case is one row and its steps are a numbered list in one cell.
 */
export type StepLayout = 'rows' | 'cell';

export interface ImportCandidate {
  title: string;
  description?: string;
  preconditions?: string;
  testData?: string;
  /** Only when the file says something this system recognises. */
  priority?: CasePriority;
  /** Names as the file wrote them; the API matches them to the project. */
  labels?: string[];
  environment?: string;
  layer?: string;
  module?: string;
  steps: Array<{ action: string; expectedResult?: string }>;
  /** Row of the file this case starts at. */
  sourceRow: number;
  sourceKey?: string;
}

/**
 * Header names seen in the exports of the common systems, in both languages.
 * Matching is on the normalised header, so "Steps (Step)" and "steps_step"
 * are the same thing.
 */
const SYNONYMS: Record<ImportField, string[]> = {
  title: [
    'title',
    'name',
    'summary',
    'testcase',
    'testcasename',
    'testcasetitle',
    'casename',
    'scenario',
    'название',
    'наименование',
    'заголовок',
    'тесткейс',
    'имя',
  ],
  description: [
    'description',
    'objective',
    'details',
    'goal',
    'testcasedescription',
    'описание',
    'цель',
  ],
  preconditions: [
    'precondition',
    'preconditions',
    'preconditon',
    'prerequisite',
    'prerequisites',
    'setup',
    'предусловие',
    'предусловия',
    'предварительныеусловия',
  ],
  testData: ['testdata', 'data', 'teststeptestdata', 'тестовыеданные', 'данные'],
  stepAction: [
    'step',
    'steps',
    'stepaction',
    'stepdescription',
    'stepsstep',
    'action',
    'teststep',
    'teststeps',
    'teststepdescription',
    'testscript',
    'testscriptstepbystep',
    'testscriptstepbystepstep',
    'stepbystep',
    'шаг',
    'шаги',
    'действие',
    'действия',
  ],
  stepExpected: [
    'expectedresult',
    'expectedresults',
    'expected',
    'stepsexpectedresult',
    'teststepexpectedresult',
    'testscriptstepbystepexpectedresult',
    'result',
    'ожидаемыйрезультат',
    'ожидаемыерезультаты',
    'результат',
  ],
  priority: ['priority', 'prio', 'importance', 'приоритет', 'важность'],
  labels: ['labels', 'label', 'tags', 'tag', 'метки', 'метка', 'теги', 'тег'],
  environment: ['environment', 'env', 'stand', 'окружение', 'стенд', 'среда'],
  layer: ['layer', 'testlayer', 'level', 'testlevel', 'слой', 'уровень'],
  module: [
    'module',
    'component',
    'feature',
    'subsystem',
    'area',
    'модуль',
    'компонент',
    'функционал',
    'раздел',
  ],
  groupKey: ['id', 'key', 'caseid', 'casekey', 'testcaseid', 'testcasekey', 'ключ', 'идентификатор'],
};

const normalise = (header: string): string =>
  header
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-zа-яё0-9]/g, '');

/**
 * What the other systems' priorities mean here. Exports write the same four
 * levels as words, letters or numbers, so all three are understood; anything
 * unrecognised leaves the case on the default this system assigns.
 */
const PRIORITY_WORDS: Array<[CasePriority, string[]]> = [
  ['low', ['low', 'lowest', 'minor', 'trivial', 'p3', '3', 'низкий', 'низкая']],
  ['medium', ['medium', 'normal', 'moderate', 'average', 'p2', '2', 'средний', 'средняя']],
  ['high', ['high', 'major', 'p1', '1', 'высокий', 'высокая']],
  [
    'critical',
    [
      'critical',
      'blocker',
      'urgent',
      'highest',
      'p0',
      '0',
      'критический',
      'критическая',
      'критичный',
      'блокер',
    ],
  ],
];

/** Matched on the normalised value, the way headers are. */
const PRIORITIES = new Map(
  PRIORITY_WORDS.flatMap(([priority, words]) =>
    words.map((word) => [normalise(word), priority] as const),
  ),
);

/** A label cell holds several names, written with commas or semicolons. */
const splitLabels = (value: string): string[] =>
  value
    .split(/[,;|\n]/)
    .map((name) => name.trim())
    .filter((name) => name !== '');

/** Best-effort match of every field onto a column of the file. */
export const detectMapping = (headers: string[]): ColumnMapping => {
  const normalised = headers.map(normalise);
  const taken = new Set<number>();

  const pick = (field: ImportField): number | null => {
    const candidates = SYNONYMS[field];
    // An exact header wins over one that merely contains the word, so that
    // "Expected result" is not taken by "Step" in "Steps (Expected Result)".
    for (const matcher of [
      (value: string) => candidates.includes(value),
      (value: string) => candidates.some((word) => word.length > 3 && value.includes(word)),
    ]) {
      const index = normalised.findIndex((value, column) => !taken.has(column) && matcher(value));
      if (index !== -1) {
        taken.add(index);
        return index;
      }
    }
    return null;
  };

  // Order matters: the more specific fields claim their column first.
  const mapping = {} as ColumnMapping;
  for (const field of [
    'stepExpected',
    'stepAction',
    'preconditions',
    'testData',
    'priority',
    'labels',
    'environment',
    'layer',
    'module',
    'description',
    'title',
    'groupKey',
  ] as const) {
    mapping[field] = pick(field);
  }
  return mapping;
};

const NUMBERED_STEP = /^\s*(\d+)\s*[.)]\s+/;

/**
 * Splits a cell holding several steps. Numbered lists are split on their
 * numbers so that a step wrapping onto a second line stays one step;
 * everything else is split per line.
 */
export const splitStepCell = (value: string): string[] => {
  const lines = value.split('\n');
  const numbered = lines.filter((line) => NUMBERED_STEP.test(line)).length;

  if (numbered >= 2) {
    const steps: string[] = [];
    for (const line of lines) {
      if (NUMBERED_STEP.test(line)) steps.push(line.replace(NUMBERED_STEP, '').trim());
      else if (steps.length > 0 && line.trim() !== '') {
        steps[steps.length - 1] = `${steps[steps.length - 1]}\n${line.trim()}`;
      }
    }
    return steps.filter((step) => step !== '');
  }

  return lines.map((line) => line.replace(NUMBERED_STEP, '').trim()).filter((line) => line !== '');
};

/**
 * Which way the file writes steps. A file where cases repeat across rows is
 * decided by that; otherwise a step cell holding a list gives it away.
 */
export const detectStepLayout = (table: SheetTable, mapping: ColumnMapping): StepLayout => {
  const cell = (row: string[], column: number | null) =>
    column === null ? '' : (row[column] ?? '');

  const repeats = (() => {
    const seen = new Set<string>();
    for (const row of table.rows) {
      const key = cell(row, mapping.groupKey) || cell(row, mapping.title);
      if (key === '') return true; // a blank title continues the case above
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  })();
  if (repeats) return 'rows';

  const multiline = table.rows.some((row) => splitStepCell(cell(row, mapping.stepAction)).length > 1);
  return multiline ? 'cell' : 'rows';
};

/** Builds the cases exactly as they will be sent to the API. */
export const buildCases = (
  table: SheetTable,
  mapping: ColumnMapping,
  layout: StepLayout,
): ImportCandidate[] => {
  const cell = (row: string[], column: number | null) =>
    column === null ? '' : (row[column] ?? '').trim();

  const cases: ImportCandidate[] = [];
  let current: ImportCandidate | null = null;
  let currentKey = '';

  table.rows.forEach((row, index) => {
    const sourceRow = table.firstRowNumber + index;
    const title = cell(row, mapping.title);
    const key = cell(row, mapping.groupKey);

    const continues =
      layout === 'rows' &&
      current !== null &&
      (key !== '' ? key === currentKey : title === '' || title === current.title);

    if (!continues) {
      current = {
        title,
        steps: [],
        sourceRow,
        ...(key ? { sourceKey: key } : {}),
      };
      currentKey = key;
      cases.push(current);
    }

    const target = current;
    if (!target) return;

    // The first row that carries a value wins: continuation rows of the same
    // case are usually blank in these columns, and sometimes repeat it.
    for (const field of [
      'description',
      'preconditions',
      'testData',
      'environment',
      'layer',
      'module',
    ] as const) {
      const value = cell(row, mapping[field]);
      if (value !== '' && target[field] === undefined) target[field] = value;
    }

    const priority = PRIORITIES.get(normalise(cell(row, mapping.priority)));
    if (priority !== undefined && target.priority === undefined) target.priority = priority;

    const labels = splitLabels(cell(row, mapping.labels));
    if (labels.length > 0 && target.labels === undefined) target.labels = labels;

    const action = cell(row, mapping.stepAction);
    const expected = cell(row, mapping.stepExpected);

    if (layout === 'rows') {
      if (action !== '') {
        target.steps.push({ action, ...(expected ? { expectedResult: expected } : {}) });
      }
      return;
    }

    const actions = splitStepCell(action);
    const expectations = splitStepCell(expected);
    actions.forEach((stepAction, stepIndex) => {
      const stepExpected =
        // A single expected result belongs to the last step, not the first.
        expectations.length === actions.length
          ? expectations[stepIndex]
          : stepIndex === actions.length - 1
            ? expectations.join('\n')
            : undefined;
      target.steps.push({
        action: stepAction,
        ...(stepExpected ? { expectedResult: stepExpected } : {}),
      });
    });

    if (actions.length === 0 && expected !== '' && target.steps.length === 0) {
      // Nothing to act on, but the expected result is worth keeping.
      target.steps.push({ action: expected });
    }
  });

  return cases;
};
